import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { RequestParameters } from '~/backend/backend-client';
import { getStructureSetRepository } from '~/domain/ServiceContainer';
import { environmentState } from '~/state/atoms/engineAtoms';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { autoSegmentationTaskState } from '~/state/atoms/structureSet/autoSegmentationTaskAtom';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    WorkflowTypes,
    pseudonymizeState,
    workflowModalState,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import useProcessDicomFiles from '../../../useProcessDicomFiles';
import useViewportEnvironment from '../../../useViewportEnvironment';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useContouringBackend, useSessionID } from '../useBackend';

/**
 * A React hook for creating an auto-segmentation task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export function useAutoSegmentation() {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const primaryImage = useRecoilValue(primaryImageState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const environment = useRecoilValue(environmentState);
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [autoSegmentationTask, setAutoSegmentationTaskState] = useRecoilState(
        autoSegmentationTaskState
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const contouringClient = useContouringBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID, sessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentStructureSet } = useViewportEnvironment();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const finalFiles = job.payload?.pseudonymized
                ? await reidentifyFiles(job.results)
                : job.results;

            if (finalFiles) {
                const generationModel = transformTaskToGenerationModel(
                    job.task
                );
                const processedItems = await processCreatedFiles(
                    finalFiles,
                    generationModel
                );
                if (processedItems) {
                    updateJob(job.id, { isImported: true });
                    setEnvironmentStructureSet(
                        processedItems.structureSets,
                        selectedPatientId ?? ''
                    );
                }
            } else {
                handleError('Re-identification failed for one or more files.');
            }
        }
    };

    const handleSubmit = async (
        selectedModel: PredictionModel,
        selectedStructures: string[],
        customizationName?: string
    ) => {
        if (isSubmitting) {
            return;
        }

        if (primaryImage) {
            setIsSubmitting(true);
            try {
                resetSessionID();
                const taskUID = `${contouringClient.sessionID}.${v4().split('-')[0]}`;
                const blobs = Array.from(primaryImage.getSlicesAsDicomBlobs());
                if (
                    autoSegmentationTask?.includeStructureSet &&
                    currentStructureSet &&
                    environment
                ) {
                    environment.saveChanges();
                    const repository = getStructureSetRepository();
                    const groundTruthStructureSet = repository.getGroundTruth(
                        currentStructureSet.dicomUid
                    );
                    blobs.push(groundTruthStructureSet.export());
                }

                const blobsToUpload = pseudonymize
                    ? await anonymizeBlobs(taskUID, blobs)
                    : blobs;

                setAutoSegmentationTaskState((state) => ({
                    ...state,
                    model: selectedModel,
                    subSelectedStructures: selectedStructures,
                    customizationName,
                }));

                const modelIdentifier = customizationName
                    ? `${selectedModel.name}.${customizationName}`
                    : selectedModel.name;

                const backendParameters: RequestParameters = {
                    action: modelIdentifier,
                    taskUID,
                    nExpectedFiles: blobs.length.toString(),
                };
                if (selectedStructures.length > 0) {
                    backendParameters.rois = JSON.stringify(selectedStructures);
                }

                if (blobsToUpload) {
                    const contourJob: NewJobData = {
                        id: sessionID,
                        name: 'Contour+',
                        totalItems: blobsToUpload.length,
                        type: WorkflowTypes.CONTOUR,
                        client: contouringClient,
                        onLoad: handleProcessedFiles,
                        payload: {
                            filesToUpload: blobsToUpload.map((file) => ({
                                file,
                            })),
                            backendParameters,
                            pseudonymized: pseudonymize,
                        },
                        task: {
                            model: selectedModel,
                            subSelectedStructures: selectedStructures,
                            includeStructureSet:
                                autoSegmentationTask?.includeStructureSet,
                        },
                    };

                    addJob(contourJob);
                    setWorkflowModalState((state) => ({
                        ...state,
                        open: false,
                    }));
                    navigate('/contouring');
                } else {
                    handleError('Anonymization failed for one or more files.');
                }
            } catch (error) {
                handleError(error, 'Failed to start auto-segmentation task.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return { startProcessing: handleSubmit, isSubmitting };
}
